import { ChangeDetectionStrategy, Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { EnterprisesRepository, ExpiredEnterpriseWalletStatus } from '@eceos/manager-domain';
import { SupportBankSlipsRepository, SupportBankSlip } from '@eceos/domain';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'app-inactive',
  templateUrl: './inactive.component.html',
  styleUrls: ['./inactive.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InactiveComponent implements OnInit {
  loading = false;

  inactiveReason = InactiveReason.DEFAULT;

  pendingBankSlips: SupportBankSlip[] = [];

  private tenantId?: string;

  constructor(
    private changeDetector: ChangeDetectorRef,
    private activatedRoute: ActivatedRoute,
    private enterprisesRepository: EnterprisesRepository,
    private supportBankSlipsRepository: SupportBankSlipsRepository
  ) {
    this.activatedRoute.queryParams.subscribe((params) => {
      this.tenantId = params['tenant'];
    });
  }

  get isDefaultReason(): boolean {
    return this.inactiveReason == InactiveReason.DEFAULT;
  }

  get isFreeCreditExpiredReason(): boolean {
    return this.inactiveReason == InactiveReason.FREE_CREDIT_EXPIRED;
  }

  get hasPendingBankSlips(): boolean {
    return this.pendingBankSlips.length > 0;
  }

  async ngOnInit() {
    if (this.tenantId) {
      this.loading = true;
      this.changeDetector.markForCheck();
      try {
        const enterprise = await lastValueFrom(this.enterprisesRepository.find(this.tenantId));
        const status = enterprise.wallet.status as ExpiredEnterpriseWalletStatus;
        if (status?.reason?.isExpiredReason) {
          this.inactiveReason = InactiveReason.FREE_CREDIT_EXPIRED;
        } else {
          this.inactiveReason = InactiveReason.DEFAULT;
        }
        await this.loadPendingBankSlips();
      } finally {
        this.loading = false;
        this.changeDetector.markForCheck();
      }
    }
  }

  private async loadPendingBankSlips() {
    try {
      const allSlips = await lastValueFrom(this.supportBankSlipsRepository.listAll());
      this.pendingBankSlips = allSlips.filter(slip => !slip.isPaid && !slip.isReversed && !slip.isFinalized);
    } catch {
      this.pendingBankSlips = [];
    }
  }
}

class InactiveReason {
  static DEFAULT = new InactiveReason('DEFAULT');
  static FREE_CREDIT_EXPIRED = new InactiveReason('FREE_CREDIT_EXPIRED');
  private constructor(readonly id: string) {}
}
